import { AttachFile } from "@mui/icons-material";
import { Card, Grid, List, ListItem, ListItemText, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { FormattedMessage } from "react-intl";
import { Email, EmailNav } from "../emailTypes";
import { formatSentMailDateBySameDay } from "../emailUtils";
import { MobileHeadBarVariant } from "./HeadBar.mobile";
import { ListItemAvatar } from "@mui/material";
import { Avatar } from "@mui/material";

const EmailCardListing: React.FC<any> = ({
	emails,
	selectedMail,
	onShowEmail,
	setMobileHeadbarVariant,
	setEmailSubject,
	selectedNav,
}) => {
	const classes = containerStyles();
	const filteredMails = emails.filter((email) => {
		if (selectedNav === EmailNav.INBOX) {
			return email.category === "inbox";
		}
		if (selectedNav === EmailNav.DRAFT) {
			return email.category === "draft";
		}
		if (selectedNav === EmailNav.OUTBOX) {
			return email.category === "sent";
		}
		if (selectedNav === EmailNav.TRASH) {
			return email.category === "trash";
		}
		return false;
	});

	const areEmailsPresent = filteredMails.length > 0;
	return (
		<Grid item container direction="column" md={7} xs={12} className={classes.container}>
			{!areEmailsPresent ? (
				<Grid item className={classes.noEmail} sx={{ fontSize: "0.75rem" }}>
					<FormattedMessage id="EMAIL.no-emails" />
				</Grid>
			) : null}
			<Grid item className={classes.emails}>
				<List component="nav" className={classes.list}>
					{filteredMails.map((email, index) => (
						<MailCardItem
							key={email.id}
							email={email}
							selectedMail={selectedMail}
							onShowEmail={onShowEmail}
							setEmailSubject={setEmailSubject}
							setMobileHeadbarVariant={setMobileHeadbarVariant}
							testId={`email-${index}`}
						/>
					))}
				</List>
			</Grid>
		</Grid>
	);
};

const MailCardItem: React.FC<{
	email: Email;
	selectedMail?: string;
	onShowEmail: (emailId: string) => void;
	setEmailSubject: (subject: string) => void;
	setMobileHeadbarVariant: (MobileHeadBarVariant) => void;
	testId: string;
}> = ({ setEmailSubject, setMobileHeadbarVariant, testId, ...props }) => {
	const { email, selectedMail, onShowEmail } = props;
	const user = email.category === "sent" ? email.receiver : email.sender;
	const classes = mailCardStyles({ selectedMail: selectedMail, emailId: email.id, avatar: user.avatarColor });
	return (
		<Card
			className={classes.cardContainer}
			sx={{
				borderLeft: (theme) => (!email.isRead ? "3px solid " + theme.palette.primary.main : "none"),
				borderBottom: isMobile ? "none" : "1px solid #DDDDDD",
				borderRadius: 0,
			}}
			data-testid={testId}
		>
			<ListItem
				button
				onClick={() => {
					onShowEmail(email.id);
					if (isMobile) {
						setEmailSubject(email.mailSubject);
						setMobileHeadbarVariant(MobileHeadBarVariant.EMAIL);
					}
				}}
				selected={selectedMail === email.id}
				className={classes.cardSubContainer}
			>
				<ListItemAvatar className={classes.listAvatar}>
					<Avatar className={classes.avatar}>{user.name.match(/\b(\w)/g)?.join("")}</Avatar>
				</ListItemAvatar>
				<ListItemText
					primary={
						<Typography variant="subtitle2" className={classes.name}>
							{user.name}
							{email.attachments?.length !== 0 && isDesktop && (
								<AttachFile className={classes.attachFile} />
							)}
							{isMobile && (
								<span className={classes.mobileDate}>
									{formatSentMailDateBySameDay(email.sentDate)}
								</span>
							)}
						</Typography>
					}
					secondary={
						<React.Fragment>
							<Typography component="span" variant="body2" className={classes.subject}>
								<b> {email.mailSubject?.slice(0, 35) + "..."}</b>
								{isDesktop && <span>{formatSentMailDateBySameDay(email.sentDate)}</span>}
							</Typography>
							<Typography
								component="span"
								variant="body2"
								color="textSecondary"
								className={classes.emailContent}
							>
								{email.mailContent
									?.replace(/<[^>]*>?/gm, "")
									?.replace(/&nbsp;/gi, "")
									?.slice(0, 35) + "..."}
							</Typography>
						</React.Fragment>
					}
				/>
			</ListItem>
		</Card>
	);
};

export default EmailCardListing;

const containerStyles = makeStyles((theme) => ({
	container: {
		flexWrap: "nowrap",
		height: "100%",
		backgroundColor: "#F8F8F8",
		borderRight: "1px solid #DDDDDD",
		borderLeft: "1px solid #DDDDDD",
		// boxShadow: "6px 0 6px -10px inset, -6px 0 6px -10px inset",
		// flexBasis: "66.66%",
		// maxWidth: "66.66%",
		// [theme.breakpoints.down("mobile")]: {
		// 	flexBasis: "100%",
		// 	maxWidth: "100%",
		// },
	},
	priority: {
		alignSelf: "flex-end",
		display: "flex",
		padding: "15px 15px",
	},
	noEmail: {
		display: "flex",
		justifyContent: "center",
		padding: "15px",
	},
	emails: {
		height: "100%",
		overflow: "auto",
		display: "flex",
		flexDirection: "column",
	},
	todayOrWeekEmail: {
		marginLeft: 20,
	},
	list: {
		paddingTop: 0,
	},
}));

const mailCardStyles = makeStyles((theme) => ({
	cardContainer: {
		// margin: 15,
		boxShadow: "none",
		[theme.breakpoints.down("mobile")]: {
			margin: 0,
			borderRadius: 0,
		},
	},
	cardSubContainer: {
		// backgroundColor: (props: any) => (props.selectedMail === props.emailId ? "rgba(0, 0, 0, 0.2)" : undefined),
	},
	name: {
		fontWeight: "bold",
		display: "flex",
		justifyContent: "space-between",
		fontSize: "0.875rem",
		[theme.breakpoints.down("mobile")]: {},
	},
	subject: {
		fontSize: "0.75rem",
		display: "flex",
		justifyContent: "space-between",
	},
	mobileDate: {
		fontSize: "0.75rem",
		fontWeight: "normal",
	},
	attachFile: {
		fontSize: "1rem",
	},
	emailContent: {
		fontSize: "0.625rem",
	},
	// listAvatar: {
	// 	[theme.breakpoints.down("mobile")]: {
	// 		minWidth: 38,
	// 		alignSelf: "flex-start",
	// 		marginTop: 6,
	// 	},
	// },
	avatar: {
		backgroundColor: (props: any) => props.avatar,
		[theme.breakpoints.down("mobile")]: {
			width: 30,
			height: 30,
		},
	},
}));
